import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useFadeInOnScroll } from '../hooks/useFadeInOnScroll';
import {
  SparklesIcon,
  LinkIcon,
  ChartBarIcon,
  CheckIcon,
  ArrowRightIcon,
  ClockIcon,
  ShieldCheckIcon,
  BoltIcon,
  CogIcon,
} from '@heroicons/react/24/outline';

const HomePage: React.FC = () => {
  const heroRef = useFadeInOnScroll();
  const featuresRef = useFadeInOnScroll();
  const howItWorksRef = useFadeInOnScroll();
  const dataRef = useFadeInOnScroll();
  const pricingRef = useFadeInOnScroll();
  
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'yearly'>('monthly');

  const features = [
    {
      icon: SparklesIcon,
      title: 'AI-Powered Insights',
      description: 'LLM-enhanced trend analysis with business opportunities and market predictions.',
    },
    {
      icon: LinkIcon,
      title: 'Multi-platform Data',
      description: 'Comprehensive data from multiple social platforms and sources.',
    },
    {
      icon: ChartBarIcon,
      title: 'Real-time Analytics',
      description: 'Live engagement scores, viral potential, and trend growth metrics.',
    },
  ];

  const howItWorks = [
    {
      step: 1,
      title: 'Sign up & get API key',
      description: 'Create your account and receive your unique API key instantly.',
    },
    {
      step: 2,
      title: 'Make API calls to access trend data',
      description: 'Use our RESTful API to fetch real-time trend information.',
    },
    {
      step: 3,
      title: 'Get insights with business opportunities',
      description: 'Receive AI-powered insights and actionable business recommendations.',
    },
  ];

  const sampleTrends = [
    {
      name: 'Y2K Aesthetic Revival',
      category: 'fashion',
      engagement_score: 1247,
      growth_rate: 34.2,
      trend_strength: 0.89,
      viral_potential: 0.92,
      momentum_score: 0.78,
      post_count: 156,
    },
    {
      name: 'Glass Skin Routine',
      category: 'beauty',
      engagement_score: 892,
      growth_rate: 28.7,
      trend_strength: 0.76,
      viral_potential: 0.84,
      momentum_score: 0.65,
      post_count: 203,
    },
    {
      name: 'Coastal Grandmother Style',
      category: 'fashion',
      engagement_score: 567,
      growth_rate: 42.1,
      trend_strength: 0.68,
      viral_potential: 0.71,
      momentum_score: 0.82,
      post_count: 89,
    },
  ];

  const pricingPlans = [
    {
      name: 'Starter + Free Trial',
      monthlyPrice: '£49',
      yearlyPrice: '£39',
      description: 'Essential Fashion & Beauty Intelligence',
      features: [
        '200 API calls/month',
        'Fashion + Beauty trends',
        'Basic engagement metrics',
        '3 smart alerts',
        'Bi-weekly reports',
      ],
      cta: 'Start Free Trial',
      popular: false,
      hasTrial: true,
    },
    {
      name: 'Growth',
      monthlyPrice: '£89',
      yearlyPrice: '£69',
      description: 'Enhanced Analytics for Growing Businesses',
      features: [
        '500 API calls/month',
        'Enhanced data sources',
        'Weekly reports',
        '7-day forecasting',
        'Basic webhooks',
      ],
      cta: 'Get Started',
      popular: true,
      hasTrial: false,
    },
    {
      name: 'Professional',
      monthlyPrice: '£129',
      yearlyPrice: '£99',
      description: 'Advanced Predictions & Forecasting',
      features: [
        '1,000 API calls/month',
        'Advanced analytics',
        'Daily briefings',
        '14-day forecasting',
        'Advanced webhooks',
      ],
      cta: 'Get Started',
      popular: false,
      hasTrial: false,
    },
    {
      name: 'Business',
      monthlyPrice: '£299',
      yearlyPrice: '£224',
      description: 'Complete Intelligence Platform',
      features: [
        '5,000 API calls/month',
        'All data sources',
        'Full ML prediction suite',
        'White-label reports',
        '30-day forecasting',
      ],
      cta: 'Get Started',
      popular: false,
      hasTrial: false,
    },
  ];

  return (
    <div className="bg-white">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-purple-50 to-indigo-100 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div ref={heroRef.ref} className="text-center">
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: heroRef.isVisible ? 1 : 0, y: heroRef.isVisible ? 0 : 20 }}
              transition={{ duration: 0.6 }}
              className="text-4xl md:text-6xl font-bold text-gray-900 mb-6"
            >
              Real-time Fashion & Beauty
              <span className="text-purple-600"> Trend Data</span>
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="text-xl text-gray-600 mb-8 max-w-3xl mx-auto"
            >
              Access comprehensive trend analytics with AI-powered insights to drive your business decisions.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="flex flex-col sm:flex-row gap-4 justify-center"
            >
              <Link
                to="/signup"
                className="bg-purple-600 text-white px-8 py-3 rounded-lg font-medium hover:bg-purple-700 transition-colors inline-flex items-center justify-center"
              >
                Start Free Trial
                <ArrowRightIcon className="ml-2 h-5 w-5" />
              </Link>
              <Link
                to="/docs"
                className="border border-gray-300 text-gray-700 px-8 py-3 rounded-lg font-medium hover:bg-gray-50 transition-colors"
              >
                View Documentation
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div ref={featuresRef.ref} className="text-center mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Everything you need to stay ahead
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Powerful features designed to give you the insights you need to make data-driven decisions.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: featuresRef.isVisible ? 1 : 0, y: featuresRef.isVisible ? 0 : 20 }}
                transition={{ duration: 0.6, delay: index * 0.2 }}
                className="text-center"
              >
                <div className="w-16 h-16 bg-purple-100 rounded-lg flex items-center justify-center mx-auto mb-6">
                  <feature.icon className="h-8 w-8 text-purple-600" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4">{feature.title}</h3>
                <p className="text-gray-600">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div ref={howItWorksRef.ref} className="text-center mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              How it works
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Get started in minutes with our simple three-step process.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {howItWorks.map((step, index) => (
              <motion.div
                key={step.step}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: howItWorksRef.isVisible ? 1 : 0, y: howItWorksRef.isVisible ? 0 : 20 }}
                transition={{ duration: 0.6, delay: index * 0.2 }}
                className="text-center"
              >
                <div className="w-12 h-12 bg-purple-600 rounded-full flex items-center justify-center mx-auto mb-6">
                  <span className="text-white font-bold text-lg">{step.step}</span>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4">{step.title}</h3>
                <p className="text-gray-600">{step.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Data Preview Section */}
      <section id="features" className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div ref={dataRef.ref} className="text-center mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Discovered trends in action
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              AI-powered unsupervised trend discovery from real fashion & beauty data.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {sampleTrends.map((trend, index) => (
              <motion.div
                key={trend.name}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: dataRef.isVisible ? 1 : 0, y: dataRef.isVisible ? 0 : 20 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm hover:shadow-md transition-shadow"
              >
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-900">{trend.name}</h3>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                    trend.category === 'fashion' ? 'bg-pink-100 text-pink-800' :
                    'bg-purple-100 text-purple-800'
                  }`}>
                    {trend.category}
                  </span>
                </div>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Engagement Score</span>
                    <span className="text-sm font-medium">{trend.engagement_score}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Growth Rate</span>
                    <span className="text-sm font-medium text-green-600">+{trend.growth_rate}%</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Trend Strength</span>
                    <span className="text-sm font-medium">{(trend.trend_strength * 100).toFixed(0)}%</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Viral Potential</span>
                    <span className="text-sm font-medium text-orange-600">{(trend.viral_potential * 100).toFixed(0)}%</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Momentum</span>
                    <span className="text-sm font-medium text-blue-600">{(trend.momentum_score * 100).toFixed(0)}%</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Posts Analyzed</span>
                    <span className="text-sm font-medium">{trend.post_count}</span>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Preview Section */}
      <section id="pricing" className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div ref={pricingRef.ref} className="text-center mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Simple, transparent pricing
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto mb-8">
              Choose the plan that fits your needs. All plans include our core features.
            </p>
            
            {/* Billing Cycle Toggle */}
            <div className="inline-flex items-center space-x-4 mb-8">
              <span className={`text-sm font-medium ${billingCycle === 'monthly' ? 'text-gray-900' : 'text-gray-500'}`}>
                Monthly
              </span>
              <button
                onClick={() => setBillingCycle(billingCycle === 'monthly' ? 'yearly' : 'monthly')}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  billingCycle === 'yearly' ? 'bg-purple-600' : 'bg-gray-200'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    billingCycle === 'yearly' ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
              <span className={`text-sm font-medium ${billingCycle === 'yearly' ? 'text-gray-900' : 'text-gray-500'}`}>
                Yearly
              </span>
            </div>
            {billingCycle === 'yearly' && (
              <p className="text-sm text-green-600 mb-4">Save up to 25% with yearly billing</p>
            )}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6 lg:gap-8">
            {pricingPlans.map((plan, index) => (
              <motion.div
                key={plan.name}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: pricingRef.isVisible ? 1 : 0, y: pricingRef.isVisible ? 0 : 20 }}
                transition={{ duration: 0.6, delay: index * 0.2 }}
                className={`bg-gradient-to-br from-white to-gray-50 rounded-xl shadow-lg border-2 p-8 relative flex flex-col h-full transition-all duration-300 hover:shadow-xl hover:-translate-y-1 ${
                  plan.popular ? 'border-purple-500 ring-2 ring-purple-200' : 'border-gray-200'
                }`}
              >
                {plan.popular && (
                  <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 z-10 min-w-max">
                    <span className="bg-gradient-to-r from-purple-500 to-purple-600 text-white px-4 py-1.5 rounded-full text-sm font-bold shadow-lg whitespace-nowrap">
                      Most Popular
                    </span>
                  </div>
                )}
                <div className="text-center mb-8 flex-shrink-0">
                  {plan.hasTrial && (
                    <div className="mb-3 h-8 flex items-center justify-center">
                      <span className="inline-flex items-center px-3 py-1.5 bg-green-50 border border-green-200 rounded-full text-sm font-medium text-green-700">
                        7-day free trial
                      </span>
                    </div>
                  )}
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">{plan.name}</h3>
                  <div className="mb-4 h-16 flex flex-col justify-center">
                    {plan.hasTrial ? (
                      <div>
                        <span className="text-lg font-bold text-green-600">Free Trial</span>
                        <div className="text-sm text-gray-500">
                          Then {billingCycle === 'yearly' ? plan.yearlyPrice : plan.monthlyPrice}/month
                        </div>
                      </div>
                    ) : (
                      <div>
                        <span className="text-4xl font-bold text-gray-900">
                          {billingCycle === 'yearly' ? plan.yearlyPrice : plan.monthlyPrice}
                        </span>
                        <span className="text-gray-600">/month</span>
                      </div>
                    )}
                  </div>
                  <p className="text-gray-600">{plan.description}</p>
                </div>
                <ul className="space-y-4 mb-8 flex-grow">
                  {plan.features.map((feature) => (
                    <li key={feature} className="flex items-center">
                      <CheckIcon className="h-5 w-5 text-green-500 mr-3 flex-shrink-0" />
                      <span className="text-gray-700">{feature}</span>
                    </li>
                  ))}
                </ul>
                <div className="mt-auto flex justify-center">
                  <Link
                    to={plan.name === 'Business' ? '/contact' : `/signup?plan=${plan.name.toLowerCase().replace(' + free trial', '')}&billingCycle=${billingCycle}`}
                    className={`w-full py-3 px-4 rounded-lg font-medium text-center transition-colors ${
                      plan.popular
                        ? 'bg-gradient-to-r from-purple-600 to-purple-700 text-white hover:from-purple-700 hover:to-purple-800 shadow-lg'
                        : 'bg-gradient-to-r from-gray-100 to-gray-200 text-gray-900 hover:from-gray-200 hover:to-gray-300 shadow-md'
                    }`}
                  >
                    {plan.cta}
                  </Link>
                </div>
              </motion.div>
            ))}
          </div>
          
          {/* Pricing CTA */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: pricingRef.isVisible ? 1 : 0, y: pricingRef.isVisible ? 0 : 20 }}
            transition={{ duration: 0.6, delay: 0.8 }}
            className="text-center mt-16"
          >
            <p className="text-lg text-gray-600 mb-6">
              Need more details about our plans and features?
            </p>
            <Link
              to="/pricing"
              className="inline-flex items-center px-8 py-3 border border-transparent text-base font-medium rounded-lg text-white bg-purple-600 hover:bg-purple-700 transition-colors duration-200"
            >
              View Full Pricing
              <ArrowRightIcon className="ml-2 h-5 w-5" />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Why Choose TrendBeat Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: pricingRef.isVisible ? 1 : 0, y: pricingRef.isVisible ? 0 : 20 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Why Choose TrendBeat?
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Built specifically for the fashion and beauty industry with cutting-edge AI and real-time data analysis.
            </p>
          </motion.div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                icon: SparklesIcon,
                title: 'AI-Powered Insights',
                description: 'Advanced machine learning algorithms analyse millions of data points to identify emerging trends before they go mainstream.',
              },
              {
                icon: ClockIcon,
                title: 'Real-Time Data',
                description: 'Get instant updates on trending topics, engagement metrics, and viral potential across all major social platforms.',
              },
              {
                icon: ChartBarIcon,
                title: 'Actionable Analytics',
                description: 'Transform raw data into clear, actionable insights that help you make informed business decisions.',
              },
              {
                icon: ShieldCheckIcon,
                title: 'Industry Focused',
                description: 'Specialized in fashion and beauty trends with deep understanding of what drives engagement in these industries.',
              },
              {
                icon: BoltIcon,
                title: 'Lightning Fast',
                description: 'Access trend data in seconds with our optimised API designed for high-performance applications.',
              },
              {
                icon: CogIcon,
                title: 'Easy Integration',
                description: 'Simple API integration with comprehensive documentation and SDKs for popular programming languages.',
              },
            ].map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-white rounded-lg p-6 shadow-sm border border-gray-200 hover:shadow-md transition-shadow duration-200"
              >
                <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mb-4">
                  <feature.icon className="h-6 w-6 text-purple-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">{feature.title}</h3>
                <p className="text-gray-600">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage; 